import Edit from "@mui/icons-material/Edit";
import { Box, Chip, Grid, IconButton, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { xsHeight, mdHeight } from "components/AppBar";
import { PREVIEW_ICON_GAP } from "components/PreviewFeature/constants";
import { StaffInDevelopmentIcon } from "components/PreviewFeature/StaffInDevelopmentIcon";
import { SectionComplianceIssuesButton } from "components/SectionComplianceIssuesButton";
import { tabsOffset } from "components/TabLinks";
import CopyableTypography from "components/Typography/CopyableTypography";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import {
  stateHasSubpartK,
  stateHasSubpartP,
} from "encamp-shared/src/waste/rcraSectionAvailability";
import { Issue, Permission } from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";

import { useFacilityRcraProfile, useRcraProfileIssues } from "../useFacility";
import { EditRcraProfileDialog } from "./EditRcraProfileDialog";
import {
  RCRA_EXEMPT_BOILER_ACTIVITY_LABELS,
  RCRA_LAND_TYPE_LABELS,
  RCRA_RECOGNIZED_TRADER_LABELS,
  RCRA_RECYCLER_ACTIVITY_LABELS,
  RCRA_SLAB_LABELS,
  RCRA_SUBPART_K_ENTITY_TYPE_LABELS,
  RCRA_SUBPART_P_ACTIVITY_LABELS,
  RCRA_TRANSPORTER_ACTIVITY_LABELS,
  RCRA_UNIVERSAL_WASTE_TYPE_LABELS,
  RCRA_USED_OIL_MARKETER_LABELS,
  RCRA_USED_OIL_PROCESSOR_LABELS,
  RCRA_USED_OIL_TRANSPORTER_LABELS,
  formatBoolean,
  formatList,
  formatRcraSiteLocation,
  formatRcraSiteMailingAddress,
} from "./rcraProfileLabels";
import { SectionSkeleton } from "./SectionSkeleton";
import { V2DisplayOnlyField as DisplayOnlyField } from "./V2DisplayOnlyField";

export const RCRA_PROFILE_SECTION_ID = "rcra-profile";
export const RCRA_PROFILE_SECTION_ICON = "♻️";

const SITE_LOCATION_ISSUE_KEYS = [
  "rcraStreetAddress1",
  "rcraStreetAddress2",
  "rcraCity",
  "rcraState",
  "rcraZipCode",
  "rcraCountry",
  "rcraCounty",
  "rcraLatitude",
  "rcraLongitude",
  "useLatLongAsPrimary",
] as const;

const SITE_MAILING_ISSUE_KEYS = [
  "rcraMailingStreetAddress1",
  "rcraMailingStreetAddress2",
  "rcraMailingCity",
  "rcraMailingState",
  "rcraMailingZipCode",
  "rcraMailingCountry",
] as const;

function useIssuesByKey(issues: Issue[]) {
  return useMemo(() => {
    const map = new Map<string, Issue[]>();
    for (const issue of issues) {
      if (issue.key) {
        const existing = map.get(issue.key) ?? [];
        existing.push(issue);
        map.set(issue.key, existing);
      }
    }
    return map;
  }, [issues]);
}

function collectIssues(issuesByKey: Map<string, Issue[]>, keys: ReadonlyArray<string>): Issue[] {
  return keys.flatMap((key) => issuesByKey.get(key) ?? []);
}

export const RcraProfileSection: React.FC = () => {
  const theme = useTheme();
  const { facilityId } = useParams<{ facilityId: string }>();
  const { hasPermissionForFacility } = useAuthorization();
  const { data, loading } = useFacilityRcraProfile(facilityId);
  const profile = data?.facility?.facilityRcraProfile ?? null;
  const [editOpen, setEditOpen] = useState(false);
  const canEdit = facilityId
    ? hasPermissionForFacility(facilityId, [Permission.WriteFacilityRcra])
    : false;

  const issues = useRcraProfileIssues(facilityId);
  const issuesByKey = useIssuesByKey(issues);

  const siteLocationText = useMemo(() => formatRcraSiteLocation(profile), [profile]);
  const siteLocationIssues = useMemo(
    () => collectIssues(issuesByKey, SITE_LOCATION_ISSUE_KEYS),
    [issuesByKey]
  );

  const mailingSameAsSiteLocation = useMemo(() => {
    if (!profile) return false;
    const fields: [string | null | undefined, string | null | undefined][] = [
      [profile.rcraMailingStreetAddress1, profile.rcraStreetAddress1],
      [profile.rcraMailingStreetAddress2, profile.rcraStreetAddress2],
      [profile.rcraMailingCity, profile.rcraCity],
      [profile.rcraMailingState, profile.rcraState],
      [profile.rcraMailingZipCode, profile.rcraZipCode],
      [profile.rcraMailingCountry, profile.rcraCountry],
    ];
    const hasAny = fields.some(([m]) => m !== null && m !== undefined && m !== "");
    if (!hasAny) return false;
    return fields.every(([m, s]) => (m ?? "") === (s ?? ""));
  }, [profile]);

  const mailingAddressText = useMemo(
    () =>
      mailingSameAsSiteLocation ? "Same as site location" : formatRcraSiteMailingAddress(profile),
    [profile, mailingSameAsSiteLocation]
  );
  const mailingAddressIssues = useMemo(
    () => collectIssues(issuesByKey, SITE_MAILING_ISSUE_KEYS),
    [issuesByKey]
  );

  const otherNaicsCodes = profile?.otherNaicsCodes ?? [];

  // Subpart K/P are only collected on some states' 8700-12 forms; hide their
  // display blocks for states that don't offer them (see the edit form gating).
  const showSubpartK = stateHasSubpartK(profile?.rcraState);
  const showSubpartP = stateHasSubpartP(profile?.rcraState);

  if (loading && !data?.facility) {
    return <SectionSkeleton title={`${RCRA_PROFILE_SECTION_ICON} RCRA Profile`} fieldCount={9} />;
  }

  return (
    <Box
      id={RCRA_PROFILE_SECTION_ID}
      sx={{
        scrollMarginTop: {
          xs: theme.spacing(xsHeight + tabsOffset + 11),
          md: theme.spacing(mdHeight + tabsOffset + 11),
        },
        "& .edit-icon": { opacity: 0, transition: "opacity 200ms" },
        "&:hover .edit-icon": { opacity: 1 },
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Stack direction="row" alignItems="center" gap={PREVIEW_ICON_GAP}>
          <Typography variant="h6">{RCRA_PROFILE_SECTION_ICON} RCRA Profile</Typography>
          <StaffInDevelopmentIcon flag={FeatureFlagKey.RcraFacilityProfile} />
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <SectionComplianceIssuesButton
            issues={issues}
            sectionHeader={`${RCRA_PROFILE_SECTION_ICON} RCRA Profile`}
          />
          {canEdit && (
            <Tooltip title="Edit Section">
              <IconButton
                size="small"
                className="edit-icon"
                sx={{ color: "action.active" }}
                onClick={() => setEditOpen(true)}
              >
                <Edit />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </Stack>

      <Typography variant="body2" color="text.secondary" mb={3}>
        RCRA Form 8700-12 site identification. When set, these values take precedence over the
        equivalent Facility fields for RCRAInfo submissions. State-specific RCRA fields are managed
        in the State Information section above.
      </Typography>

      {editOpen && <EditRcraProfileDialog onClose={() => setEditOpen(false)} />}

      <Stack spacing={4}>
        <Box>
          <Typography variant="subtitle2" fontWeight={600} mb={3}>
            Site Identification
          </Typography>
          <Grid container columnSpacing={3} rowSpacing={3}>
            <Grid size={{ xs: 12 }}>
              <DisplayOnlyField
                label="RCRA Site Name"
                text={profile?.rcraSiteName ?? ""}
                issues={issuesByKey.get("rcraSiteName")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Site Location"
                text={siteLocationText}
                issues={siteLocationIssues}
                renderValue={() => (
                  <CopyableTypography
                    text={siteLocationText}
                    sx={{ whiteSpace: "pre-line", wordBreak: "break-word" }}
                    tooltipOptions={{ disableHoverMessage: true }}
                  />
                )}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Site Mailing Address"
                text={mailingAddressText}
                issues={mailingAddressIssues}
                renderValue={() => (
                  <CopyableTypography
                    text={mailingAddressText}
                    sx={{ whiteSpace: "pre-line", wordBreak: "break-word" }}
                    tooltipOptions={{ disableHoverMessage: true }}
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>

        <Box>
          <Typography variant="subtitle2" fontWeight={600} mb={3}>
            Site Land Type
          </Typography>
          <Grid container columnSpacing={3} rowSpacing={3}>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Land Type"
                text={profile?.landType ? RCRA_LAND_TYPE_LABELS[profile.landType] : ""}
                issues={issuesByKey.get("landType")}
              />
            </Grid>
          </Grid>
        </Box>

        <Box>
          <Typography variant="subtitle2" fontWeight={600} mb={3}>
            NAICS Codes
          </Typography>
          <Grid container columnSpacing={3} rowSpacing={3}>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Primary NAICS Code"
                text={profile?.rcraNaicsCode?.toString() ?? ""}
                issues={issuesByKey.get("rcraNaicsCode")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Other NAICS Codes"
                renderValue={() =>
                  otherNaicsCodes.length > 0 ? (
                    <Stack direction="row" flexWrap="wrap" gap={0.5}>
                      {otherNaicsCodes.map((code) => (
                        <Chip key={code} label={code.toString()} size="small" />
                      ))}
                    </Stack>
                  ) : (
                    ""
                  )
                }
                issues={issuesByKey.get("otherNaicsCodes")}
              />
            </Grid>
          </Grid>
        </Box>

        <Box>
          <Typography variant="subtitle2" fontWeight={600} mb={3}>
            Hazardous Waste Activities
          </Typography>
          <Grid container columnSpacing={3} rowSpacing={3}>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Short-Term Generator"
                text={formatBoolean(profile?.shortTermGenerator)}
                issues={issuesByKey.get("shortTermGenerator")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 8 }}>
              <DisplayOnlyField
                label="Short-Term Generator Notes"
                text={profile?.shortTermGeneratorNotes ?? ""}
                issues={issuesByKey.get("shortTermGeneratorNotes")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Treater, Storer, or Disposer of Haz Waste (TSD)"
                text={formatBoolean(profile?.tsdActivity)}
                issues={issuesByKey.get("tsdActivity")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Receives Hazardous Waste from Off-site"
                text={formatBoolean(profile?.offSiteReceipt)}
                issues={issuesByKey.get("offSiteReceipt")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Recycler Activity"
                text={
                  profile?.recyclerActivity
                    ? RCRA_RECYCLER_ACTIVITY_LABELS[profile.recyclerActivity]
                    : ""
                }
                issues={issuesByKey.get("recyclerActivity")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 8 }}>
              <DisplayOnlyField
                label="Exempt Boiler / Industrial Furnace"
                text={formatList(
                  profile?.exemptBoilerActivities,
                  RCRA_EXEMPT_BOILER_ACTIVITY_LABELS
                )}
                issues={issuesByKey.get("exemptBoilerActivities")}
              />
            </Grid>
          </Grid>
        </Box>

        <Box>
          <Typography variant="subtitle2" fontWeight={600} mb={3}>
            Waste Activity
          </Typography>
          <Grid container columnSpacing={3} rowSpacing={3}>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Transporter Activity"
                text={
                  profile?.transporterActivity
                    ? RCRA_TRANSPORTER_ACTIVITY_LABELS[profile.transporterActivity]
                    : ""
                }
                issues={issuesByKey.get("transporterActivity")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Underground Injection Control"
                text={formatBoolean(profile?.undergroundInjectionControl)}
                issues={issuesByKey.get("undergroundInjectionControl")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="US Importer of Hazardous Waste"
                text={formatBoolean(profile?.usImporterOfHazardousWaste)}
                issues={issuesByKey.get("usImporterOfHazardousWaste")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Recognized Trader Activity"
                text={
                  profile?.recognizedTraderActivity
                    ? RCRA_RECOGNIZED_TRADER_LABELS[profile.recognizedTraderActivity]
                    : ""
                }
                issues={issuesByKey.get("recognizedTraderActivity")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="SLAB Importer / Exporter"
                text={profile?.slabImporter ? RCRA_SLAB_LABELS[profile.slabImporter] : ""}
                issues={issuesByKey.get("slabImporter")}
              />
            </Grid>
          </Grid>
        </Box>

        <Box>
          <Typography variant="subtitle2" fontWeight={600} mb={3}>
            Universal Waste (LQHUW)
          </Typography>
          <Grid container columnSpacing={3} rowSpacing={3}>
            <Grid size={{ xs: 12, sm: 6, lg: 8 }}>
              <DisplayOnlyField
                label="Universal Waste Types (LQHUW)"
                text={formatList(profile?.lqhuwActivities, RCRA_UNIVERSAL_WASTE_TYPE_LABELS)}
                issues={issuesByKey.get("lqhuwActivities")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Universal Waste Destination Facility"
                text={formatBoolean(profile?.universalWasteDestFacility)}
                issues={issuesByKey.get("universalWasteDestFacility")}
              />
            </Grid>
          </Grid>
        </Box>

        <Box>
          <Typography variant="subtitle2" fontWeight={600} mb={3}>
            Used Oil
          </Typography>
          <Grid container columnSpacing={3} rowSpacing={3}>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Used Oil Transporter Activities"
                text={formatList(
                  profile?.usedOilTransporterActivities,
                  RCRA_USED_OIL_TRANSPORTER_LABELS
                )}
                issues={issuesByKey.get("usedOilTransporterActivities")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Used Oil Processor Activities"
                text={formatList(
                  profile?.usedOilProcessorActivities,
                  RCRA_USED_OIL_PROCESSOR_LABELS
                )}
                issues={issuesByKey.get("usedOilProcessorActivities")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Off-Specification Used Oil Burner"
                text={formatBoolean(profile?.offSpecUsedOilBurner)}
                issues={issuesByKey.get("offSpecUsedOilBurner")}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, lg: 8 }}>
              <DisplayOnlyField
                label="Used Oil Marketer Activities"
                text={formatList(profile?.usedOilMarketerActivities, RCRA_USED_OIL_MARKETER_LABELS)}
                issues={issuesByKey.get("usedOilMarketerActivities")}
              />
            </Grid>
          </Grid>
        </Box>

        {showSubpartP && (
          <Box>
            <Typography variant="subtitle2" fontWeight={600} mb={3}>
              Subpart P (Pharmaceutical)
            </Typography>
            <Grid container columnSpacing={3} rowSpacing={3}>
              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <DisplayOnlyField
                  label="Subpart P Activity (Pharmaceutical)"
                  text={
                    profile?.subpartPActivity
                      ? RCRA_SUBPART_P_ACTIVITY_LABELS[profile.subpartPActivity]
                      : ""
                  }
                  issues={issuesByKey.get("subpartPActivity")}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <DisplayOnlyField
                  label="Subpart P Withdrawal"
                  text={formatBoolean(profile?.subpartPWithdrawal)}
                  issues={issuesByKey.get("subpartPWithdrawal")}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        {showSubpartK && (
          <Box>
            <Typography variant="subtitle2" fontWeight={600} mb={3}>
              Subpart K (Academic Lab)
            </Typography>
            <Grid container columnSpacing={3} rowSpacing={3}>
              <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
                <DisplayOnlyField
                  label="Subpart K Withdrawal"
                  text={formatBoolean(profile?.subpartKWithdrawal)}
                  issues={issuesByKey.get("subpartKWithdrawal")}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6, lg: 8 }}>
                <DisplayOnlyField
                  label="Subpart K Entity Types (Academic Lab)"
                  text={formatList(profile?.subpartKEntityTypes, RCRA_SUBPART_K_ENTITY_TYPE_LABELS)}
                  issues={issuesByKey.get("subpartKEntityTypes")}
                />
              </Grid>
            </Grid>
          </Box>
        )}

        <Box>
          <Typography variant="subtitle2" fontWeight={600} mb={3}>
            Manifest Broker
          </Typography>
          <Grid container columnSpacing={3} rowSpacing={3}>
            <Grid size={{ xs: 12, sm: 6, lg: 4 }}>
              <DisplayOnlyField
                label="Electronic Manifest Broker"
                text={formatBoolean(profile?.manifestBroker)}
                issues={issuesByKey.get("manifestBroker")}
              />
            </Grid>
          </Grid>
        </Box>
      </Stack>
    </Box>
  );
};
